import { Form, SubmitFormButton, TextField } from '@calendly/form'
import { object, string } from '@calendly/form/validation'
import { useOnValueChange, useQueryParams } from '@calendly/hooks'
import { HtmlParser } from '@calendly/html-parser'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { FullStoryExclude } from '@calendly/ui/components/fullstory-privacy'
import { Loader } from '@calendly/ui/components/loader'
import { Body, Footer, Header, Popup } from '@calendly/ui/components/popup'
import { useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import ErrorMessage from 'components/core/error_message'
import { useConnectExchangeMutation, useFetchExchangeDataQuery } from 'features/calendar_connections'

import { ExchangeFieldsContainer, ExchangeHelpCenterLink, FormErrorsContainer, OptionalLabel } from './styles'
import { CALENDAR_SETTINGS_PATH } from '../../constants'

const buildValidationSchema = () => object({
  email: string().email().required(),
  password: string().required()
})

const ErrorMessages = ({ error }) => {
  if (!error) return null

  return (
    <FormErrorsContainer>
      <ErrorMessage key={error} message={HtmlParser(error, { ExchangeHelpCenterLink })} />
    </FormErrorsContainer>
  )
}

export const ExchangePopup = ({ isSettingsModalLayout = false }) => {
  const history = useHistory()
  const { state } = useLocation()
  const { calendar_account_id: calendarAccountId } = useQueryParams()
  const { data, isLoading } = useFetchExchangeDataQuery(calendarAccountId, { skip: !calendarAccountId })
  const [connectExchange, { error, isSuccess }] = useConnectExchangeMutation()

  useOnValueChange(isSuccess, () => {
    if (isSuccess) {
      history.push(CALENDAR_SETTINGS_PATH)
    }
  })

  const goBackOnCancel = state && state.withGoBack

  const initialValues = {
    calendarAccountId,
    email: data?.email || '',
    server: data?.server || '',
    user: data?.externalId || '',
    password: '',
    domain: ''
  }

  const onCancel = useCallback(() => {
    if (goBackOnCancel) {
      history.goBack()
    } else {
      history.push(CALENDAR_SETTINGS_PATH)
    }
  }, [goBackOnCancel, history])
  const onClose = () => history.push(CALENDAR_SETTINGS_PATH)
  const onSubmit = (formData) => connectExchange({
    ...formData,
    skipBrowserHistoryBack: isSettingsModalLayout,
  })

  return (
    <Popup onClose={onClose} size="medium" withCloseButton>
      <Header
        title={I18n.t('publisher.availability_settings.section.calendar_settings.account_connection_popup.exchange.title')}
        subtitle={
          I18n.t('publisher.availability_settings.section.calendar_settings.account_connection_popup.exchange.description')
        }
      />
      {isLoading ? <Loader /> : (
        <Form
          initialValues={initialValues}
          validationSchema={buildValidationSchema}
          onSubmit={onSubmit}
        >
          <Body>
            <ExchangeFieldsContainer withMargin>
              <FullStoryExclude>
                <TextField
                  name="email"
                  type="email"
                  label={(
                    I18n.t('publisher.availability_settings.section.calendar_settings.account_connection_popup.exchange.email_address')
                  )}
                />
              </FullStoryExclude>
              <FullStoryExclude>
                <TextField
                  name="password"
                  type="password"
                  label={(
                    I18n.t('publisher.availability_settings.section.calendar_settings.account_connection_popup.exchange.password')
                  )}
                />
              </FullStoryExclude>
            </ExchangeFieldsContainer>
          </Body>
          <Body isInverse>
            <ExchangeFieldsContainer>
              <TextField
                name="server"
                label={(
                  <OptionalLabel>
                    {I18n.t('publisher.availability_settings.section.calendar_settings.account_connection_popup.exchange.server_endpoint')}
                  </OptionalLabel>
                )}
              />
              <TextField
                name="domain"
                label={(
                  <OptionalLabel>
                    {I18n.t('publisher.availability_settings.section.calendar_settings.account_connection_popup.exchange.domain')}
                  </OptionalLabel>
                )}
              />
              <FullStoryExclude>
                <TextField
                  name="user"
                  label={(
                    <OptionalLabel>
                      {I18n.t('publisher.availability_settings.section.calendar_settings.account_connection_popup.exchange.username')}
                    </OptionalLabel>
                  )}
                />
              </FullStoryExclude>
            </ExchangeFieldsContainer>
          </Body>
          {error && (
          <Body>
            <ErrorMessages error={error.message} />
          </Body>
          )}
          <Footer isOffset>
            <Button
              decoration="secondary-outline"
              onClick={onCancel}
              spacingTop={16}
              size="small"
            >
              {I18n.t('publisher.action.cancel')}
            </Button>
            <SubmitFormButton spacingTop={16}>
              {I18n.t('publisher.action.connect')}
            </SubmitFormButton>
          </Footer>
        </Form>
      )}
    </Popup>
  )
}
